import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { CREATE_CART_PRESCRIPTION } from '../../../apollo';
import { Cart } from '@hiwaldo/sdk/interfaces';
import { cartSlice, StoreState } from '../../../redux';
import { useCreateCartEffect } from './create-cart.effect';
import { CartPrescriptionEye } from '../../../../types';

type CreateCartPrescriptionEffectReturn = {
  createCartPrescription: (
    leftEye: CartPrescriptionEye,
    rightEye: CartPrescriptionEye
  ) => void;
  loading: boolean;
  success: boolean;
  error: string | undefined;
};

export const useCreateCartPrescriptionEffect = (
  callback?: () => void
): CreateCartPrescriptionEffectReturn => {
  const dispatch = useDispatch();
  const cart = useSelector(
    (state: StoreState) => state.orders.cart.cart
  ) as Cart;

  const { createCart } = useCreateCartEffect();

  const [doCreateCartPrescription, createCartPrescriptionState] = useMutation(
    CREATE_CART_PRESCRIPTION,
    {
      onCompleted: (data) => {
        dispatch(
          cartSlice.actions.updateCartPrescription(data.createCartPrescription)
        );

        if (callback) {
          callback();
        }
      },
    }
  );

  const { loading, called, error } = createCartPrescriptionState;

  const createCartPrescription = (
    leftEye: CartPrescriptionEye,
    rightEye: CartPrescriptionEye
  ): void => {
    if (cart) {
      const data = {
        cartId: cart.id,
        leftEyeProductVariantId: leftEye?.productVariantId || null,
        rightEyeProductVariantId: rightEye?.productVariantId || null,
      };
      doCreateCartPrescription({ variables: { data } }).then();
    } else {
      const args = { leftEye, rightEye };
      createCart((args, cartId) => {
        const variables = {
          data: {
            cartId,
            leftEyeProductVariantId: leftEye?.productVariantId || null,
            rightEyeProductVariantId: rightEye?.productVariantId || null,
          },
        };

        doCreateCartPrescription({ variables }).then();
      }, args);
    }
  };

  const success = !loading && called && !error;

  return {
    createCartPrescription,
    loading,
    success,
    error: error?.message,
  };
};
